import { useUtils } from "@/composables/useUtils";
import { useAppStore } from "@/stores/app";
import type { JourneyAccessEventPayload } from "@/types/api";
import type { RouteLocationNormalized } from "vue-router";

export const JOURNEY_ACCESS_LOGGED_KEY = "journey_access_logged";
export const JOURNEY_SESSION_ID_KEY = "journey_session_id";

const EXCLUDED_ROUTE_NAMES = new Set(["privacyPolicy"]);

export type JourneyAccessStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};

export type LogJourneyAccessDeps = {
  storage?: JourneyAccessStorage;
  createSessionId?: () => string;
  getBrand?: () => string | null;
  sendEvent: (payload: JourneyAccessEventPayload) => Promise<unknown> | undefined;
  log?: (message: string) => void;
};

export function getJourneySessionId(storage: JourneyAccessStorage = sessionStorage): string | null {
  try {
    return storage.getItem(JOURNEY_SESSION_ID_KEY);
  } catch {
    return null;
  }
}

export function journeySessionParam(
  storage: JourneyAccessStorage = sessionStorage,
): { sessionId?: string } {
  const sessionId = getJourneySessionId(storage);
  if (sessionId === null) {
    return {};
  }
  return { sessionId };
}

function buildPayload(
  to: RouteLocationNormalized,
  sessionId: string,
  getBrand: () => string | null,
): JourneyAccessEventPayload {
  return {
    sessionId,
    brand: getBrand(),
    pageUrl: location.href,
    referrer: document.referrer || null,
    userAgent: navigator.userAgent,
    storeTag: (to.query.tag as string | undefined) ?? null,
    sellerContactId: (to.query.c as string | undefined) ?? null,
    occurredAtClient: new Date().toISOString(),
  };
}

export function logJourneyAccessOnce(
  to: RouteLocationNormalized,
  deps: LogJourneyAccessDeps,
): void {
  const log = deps.log ?? ((message: string) => useUtils().log(message));

  try {
    if (EXCLUDED_ROUTE_NAMES.has(String(to.name))) {
      return;
    }

    const storage = deps.storage ?? sessionStorage;
    const createSessionId = deps.createSessionId ?? (() => crypto.randomUUID());
    const getBrand = deps.getBrand ?? (() => useAppStore().brand ?? null);
    const sendEvent = deps.sendEvent;

    if (storage.getItem(JOURNEY_ACCESS_LOGGED_KEY) === "true") {
      return;
    }

    let sessionId = storage.getItem(JOURNEY_SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = createSessionId();
      storage.setItem(JOURNEY_SESSION_ID_KEY, sessionId);
    }

    storage.setItem(JOURNEY_ACCESS_LOGGED_KEY, "true");

    const payload = buildPayload(to, sessionId, getBrand);

    sendEvent(payload)?.catch((error: unknown) => {
      log(`Failed to log journey access event: ${String(error)}`);
    });
  } catch (error) {
    log(`Failed to run journey access tracking: ${String(error)}`);
  }
}
